/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight } from "lucide-react";
import api from "@/lib/api";

function ServiceIcon({ name, className }) {
    const Cmp = Icons[name] || Icons.Briefcase;
    return <Cmp className={className} />;
}

export default function Services() {
    const [services, setServices] = useState([]);

    useEffect(() => {
        api.get("/services").then(({ data }) => setServices(data));
    }, []);

    return (
        <div data-testid="services-page">
            {/* Hero */}
            <section className="bg-[#0A192F] text-white py-24">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                    <span className="overline">Services</span>
                    <h1 className="font-serif text-5xl sm:text-6xl font-bold mt-4 leading-tight max-w-4xl">
                        Twelve disciplines.
                        <br />
                        <span className="italic text-[#D4AF37]">
                            One outcome — work that lasts.
                        </span>
                    </h1>
                    <p className="mt-8 text-slate-300 max-w-2xl leading-relaxed">
                        Every engagement is scoped to the stage you're in.
                        Below is the full menu — pick what fits, or talk to us
                        about a custom package.
                    </p>
                </div>
            </section>

            <section className="py-20">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {services.map((s, idx) => (
                        <Link
                            key={s.id}
                            to={`/services/${s.slug}`}
                            data-testid={`service-card-${s.slug}`}
                            className="exec-card p-8 flex flex-col group"
                        >
                            <div className="flex items-center justify-between mb-6">
                                <div className="w-12 h-12 bg-[#0A192F] flex items-center justify-center rounded-sm">
                                    <ServiceIcon
                                        name={s.icon}
                                        className="w-5 h-5 text-[#D4AF37]"
                                    />
                                </div>
                                <span className="num-tag">
                                    {String(idx + 1).padStart(2, "0")}
                                </span>
                            </div>
                            <h2 className="font-serif text-2xl font-bold text-[#0A192F] leading-tight">
                                {s.title}
                            </h2>
                            <p className="mt-3 text-slate-600 text-sm leading-relaxed">
                                {s.description}
                            </p>
                            {s.who_it_is_for && (
                                <div className="mt-5 pt-5 border-t border-slate-200">
                                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">
                                        Who it's for
                                    </p>
                                    <p className="text-sm text-slate-600">
                                        {s.who_it_is_for}
                                    </p>
                                </div>
                            )}
                            {s.deliverables?.length > 0 && (
                                <div className="mt-5">
                                    <p className="text-[10px] uppercase tracking-[0.2em] text-[#D4AF37] font-semibold mb-2">
                                        What you receive
                                    </p>
                                    <ul className="space-y-1.5 text-sm text-slate-600">
                                        {s.deliverables.map((d) => (
                                            <li
                                                key={d}
                                                className="flex items-start gap-2"
                                            >
                                                <span className="w-1 h-1 bg-[#D4AF37] mt-2" />
                                                {d}
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            <span className="mt-auto pt-6 text-sm font-bold uppercase tracking-widest text-[#0A192F] group-hover:text-[#D4AF37] inline-flex items-center gap-2 transition-colors">
                                Learn More & Request{" "}
                                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                            </span>
                        </Link>
                    ))}
                </div>
            </section>

            <section className="bg-slate-50 py-20">
                <div className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 text-center">
                    <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0A192F]">
                        Not sure where to start?
                    </h2>
                    <p className="mt-4 text-slate-600">
                        Book a free 30-minute strategy call. We'll diagnose
                        and recommend a path forward — no pitch.
                    </p>
                    <Link
                        to="/contact"
                        data-testid="services-bottom-cta"
                        className="mt-8 inline-flex bg-[#0A192F] text-white hover:bg-[#1E3A8A] rounded-sm px-8 py-4 font-bold text-sm uppercase tracking-widest"
                    >
                        Build Your Strategy
                    </Link>
                </div>
            </section>
        </div>
    );
}
